import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "./ui";
import { IconX, IconMoodSmile } from "@tabler/icons-react";
import { joinIconName, searchEmojis, splitIconName } from "./warehouse-emoji";

interface RenameModalProps {
  title: string;
  label: string;
  initialValue: string;
  onClose: () => void;
  onSubmit: (name: string) => void;
  /** Optional: allow prepending a searchable emoji icon to the name (開頭). */
  enableIcon?: boolean;
}

export function RenameModal({
  title,
  label,
  initialValue,
  onClose,
  onSubmit,
  enableIcon = false,
}: RenameModalProps) {
  const { t } = useTranslation();
  const parsed = useMemo(
    () => (enableIcon ? splitIconName(initialValue) : { icon: "", text: initialValue }),
    [enableIcon, initialValue]
  );

  const [icon, setIcon] = useState(parsed.icon);
  const [value, setValue] = useState(parsed.text);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");

  const results = useMemo(() => searchEmojis(query), [query]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (pickerOpen) setPickerOpen(false);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, pickerOpen]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError(t("warehouseMap.rename.nameRequired"));
      return;
    }
    onSubmit(enableIcon ? joinIconName(icon, trimmed) : trimmed);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-[#30333c] bg-[#1a1d24] shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#30333c] px-5 py-4">
          <h3 className="text-base font-semibold text-zinc-100">{title}</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <IconX className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-zinc-400">{label}</span>
            <div className="flex items-stretch gap-2">
              {enableIcon && (
                <div className="relative flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setPickerOpen((open) => !open)}
                    title={t("warehouseMap.rename.chooseIcon")}
                    className="flex h-9 w-11 items-center justify-center rounded-md border border-[#30333c] bg-[#0d0f15] text-lg leading-none text-zinc-100 transition-colors hover:bg-[#2a2d35]"
                  >
                    {icon ? (
                      <span>{icon}</span>
                    ) : (
                      <IconMoodSmile className="h-4 w-4 text-zinc-500" />
                    )}
                  </button>

                  {pickerOpen && (
                    <div className="absolute left-0 top-full z-10 mt-1 w-72 rounded-lg border border-[#30333c] bg-[#1a1d24] p-2 shadow-2xl">
                      <Input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("warehouseMap.rename.searchIcon")}
                        className="mb-2"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIcon("");
                          setPickerOpen(false);
                          setQuery("");
                        }}
                        className="mb-1 w-full rounded px-2 py-1 text-left text-xs text-zinc-400 transition-colors hover:bg-[#2a2d35] hover:text-zinc-100"
                      >
                        {t("warehouseMap.rename.noIcon")}
                      </button>
                      <div className="grid max-h-44 grid-cols-7 gap-1 overflow-y-auto">
                        {results.map(({ emoji }) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => {
                              setIcon(emoji);
                              setPickerOpen(false);
                              setQuery("");
                            }}
                            className={`flex h-8 w-8 items-center justify-center rounded text-lg leading-none transition-colors hover:bg-[#2a2d35] ${
                              icon === emoji ? "bg-teal-400/20 ring-1 ring-teal-400/60" : ""
                            }`}
                          >
                            {emoji}
                          </button>
                        ))}
                        {results.length === 0 && (
                          <div className="col-span-7 py-4 text-center text-xs text-zinc-500">
                            {t("warehouseMap.rename.noIconFound")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Input
                autoFocus
                value={value}
                onFocus={() => setPickerOpen(false)}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSubmit();
                }}
                className="flex-1"
              />
            </div>
          </label>

          {enableIcon && (
            <p className="text-xs text-zinc-500">
              {t("warehouseMap.rename.iconHint")}
              <span className="text-zinc-300">
                {t("warehouseMap.zoneLabel", { name: joinIconName(icon || "📦", value || "A") })}
              </span>
            </p>
          )}

          {error && (
            <p className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex gap-2 border-t border-[#30333c] px-5 py-4">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {t("warehouseMap.cancel")}
          </Button>
          <Button className="flex-1" onClick={handleSubmit}>
            {t("warehouseMap.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}
